import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useAuth } from 'providers/auth-provider/AuthJwtProvider';
import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';
import StyledTextField from 'components/styled/StyledTextField';

const TeamTable = () => {
  const { sessionUser } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: '',
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUser] = useState(null);

  // ================= VIEW BUTTON =================
  const ViewButton = ({ row }) => (
    <IconButton onClick={() => navigate('/my-team/user-info', { state: row })}>
      <RemoveRedEyeOutlinedIcon />
    </IconButton>
  );

  // ================= COLUMNS =================
  const columns = useMemo(
    () => [
      {
        field: 'view',
        headerName: 'View',
        sortable: false,
        filterable: false,
        minWidth: 80,
        renderCell: (params) => <ViewButton row={params.row} />,
      },
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { firstName, lastName, profileImage, empId } = params.row;

          return (
            <Stack sx={{ gap: 1.5, alignItems: 'center' }}>
              <Avatar
                alt={firstName?.toUpperCase()}
                src={profileImage || ''}
                sx={{ width: 28, height: 28 }}
              />
              <div>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  {firstName} {lastName}
                </Typography>
                <Typography variant="caption">{empId}</Typography>
              </div>
            </Stack>
          );
        },
      },
      {
        field: 'role',
        headerName: 'Designation',
        minWidth: 200,
        sortable: false,
        filterable: false,
      },
      {
        field: 'totalLeavesTaken',
        headerName: 'Total Leaves',
        minWidth: 120,
        sortable: false,
        filterable: false,
      },
      {
        field: 'lastLeaveDate',
        headerName: 'Last Leave',
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const lastLeaveDate = params.row.lastLeaveDate;
          return lastLeaveDate ? moment(lastLeaveDate).format('DD/MM/YYYY') : '-';
        },
      },
      {
        field: 'upcomingLeave',
        headerName: 'Next Leave',
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const upcomingLeave = params.row.upcomingLeave;
          return upcomingLeave ? moment(upcomingLeave).format('DD/MM/YYYY') : '-';
        },
      },
      {
        field: 'attendance',
        headerName: 'Attendance',
        minWidth: 170,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const attendance = params.row.attendance;
          const isOnLeaveToday = params.row.isOnLeaveToday;

          if (attendance?.loginTime) {
            return (
              <Typography variant="body2">
                {moment(attendance.loginTime).format('hh:mm A')} /{' '}
                {attendance.logoutTime ? moment(attendance.logoutTime).format('hh:mm A') : '-'}
              </Typography>
            );
          }

          return (
            <Chip
              label={isOnLeaveToday ? 'On Leave' : attendance?.status || 'Absent'}
              color={
                isOnLeaveToday
                  ? 'info'
                  : attendance?.status === 'In Progress'
                    ? 'warning'
                    : attendance?.status === 'Present'
                      ? 'success'
                      : 'error'
              }
            />
          );
        },
      },
    ],
    [sessionUser],
  );

  // ================= FETCH DATA =================
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await post('/admin/getTeam', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
      });

      if (res.status === 200) {
        setRows(res.data.data);
        setTotal(res.data.total);
      } else {
        enqueueSnackbar(res.message || 'Error fetching team', {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error fetching team', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // ================= SEARCH DEBOUNCE =================
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({
        ...prev,
        search,
        page: 0,
      }));
    }, 600);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [paginationModel]);

  // ================= TOGGLE STATUS =================
  const toggleUserStatus = async (userId) => {
    try {
      await post('users/user_status_change', { userId });
      enqueueSnackbar('User status updated', { variant: 'success' });
      fetchData();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to update status', { variant: 'error' });
    }
  };

  // ================= RENDER =================
  return (
    <>
      <StyledTextField
        type="search"
        fullWidth
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search Team Member"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          maxWidth: { sm: 200, md: 240 },
          mb: 3,
        }}
      />

      <Box sx={{ width: '100%' }}>
        <DataGrid
          loading={loading}
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          pagination
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) =>
            setPaginationModel({
              page: model.page,
              pageSize: model.pageSize,
              search,
            })
          }
          pageSizeOptions={[5, 10, 20]}
          slots={{
            basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
          }}
        />
      </Box>

      {/* CONFIRM DIALOG */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          Are you sure you want to <b>{selectedUser?.isActive ? 'deactivate' : 'activate'}</b> this
          user?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              toggleUserStatus(selectedUser._id);
              setConfirmOpen(false);
            }}
          >
            Yes, Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeamTable;
